import type { APPayload, APSummary } from './types';
import type { Bucket } from '@/lib/types';

export function shouldShowApprove(lifecycle: string): boolean {
  return lifecycle === 'pending_approval';
}

export function approveDisabledReason(
  lifecycle: string,
  dirty: boolean,
): string | null {
  if (dirty) return 'Save changes first';
  if (lifecycle !== 'pending_approval') {
    return 'Save & validate first to mark the row ready for approval';
  }
  return null;
}

export function servicePostDisabledReason(
  dirty: boolean,
  bucket?: Bucket | null,
): string | null {
  if (dirty) return 'Save changes first';
  if (bucket === 'EXP_REB_REVIEW') return 'Complete Exp/Reb coding first';
  return null;
}

function hasResolvedSupplier(payload: APPayload): boolean {
  const supplierId = payload.supplier_id;
  if (supplierId === null || supplierId === undefined) return false;
  return String(supplierId).trim().length > 0;
}

function hasCanonicalNoPostableGrnBucket(payload: APPayload, summary: APSummary): boolean {
  const values = [
    payload.error_type,
    payload.oracle_classification,
    summary.status_label,
  ];
  return values.some((value) => String(value ?? '').toUpperCase() === 'NO_POSTABLE_GRN_FOUND');
}

function hasServicePreflight(payload: APPayload): boolean {
  const structured = payload.structured_fields;
  if (structured && typeof structured === 'object' && !Array.isArray(structured)) {
    const preflight = (structured as Record<string, unknown>)['service_preflight'];
    if (preflight && typeof preflight === 'object' && !Array.isArray(preflight)) {
      return true;
    }
  }
  return Boolean(payload.service_preflight);
}

function hasPoOrGrnEvidence(payload: APPayload): boolean {
  const structured =
    payload.structured_fields &&
    typeof payload.structured_fields === 'object' &&
    !Array.isArray(payload.structured_fields)
      ? (payload.structured_fields as Record<string, unknown>)
      : {};
  const defaults =
    payload.default_fields &&
    typeof payload.default_fields === 'object' &&
    !Array.isArray(payload.default_fields)
      ? (payload.default_fields as Record<string, unknown>)
      : {};
  return [
    payload.po_no,
    payload.po_number,
    payload.grn_no,
    payload.grn_number,
    payload.grn_id,
    payload.grn_detail_ids,
    structured['grn_detail_ids'],
    defaults['po_no'],
    defaults['grn_no'],
  ].some((value) => {
    if (Array.isArray(value)) return value.length > 0;
    return String(value ?? '').trim().length > 0;
  });
}

/** Show the "Prepare service invoice" CTA when the user is on a row that
 * either (a) is already in the SERVICE_PREP bucket / service_blocked
 * lifecycle, (b) is a failed service-post retry, or (c) has no GRN match.
 * The button itself is purely a UI
 * trigger that opens the modal — clicking it does not mutate the row.
 * Only the modal's "Validate and post" CTA writes anything.
 */
export function shouldShowPostAsService(
  payload: APPayload,
  summary: APSummary,
  lifecycle: string,
  bucket?: Bucket | null,
): boolean {
  if (lifecycle === 'posted' || lifecycle === 'rejected') return false;
  if (lifecycle === 'service_blocked') return true;
  if (bucket === 'SERVICE_PREP') return true;
  if (bucket === 'GRN_NOT_FOUND') {
    return hasResolvedSupplier(payload) && !hasPoOrGrnEvidence(payload);
  }
  if (bucket === 'EXP_REB_REVIEW') {
    return hasResolvedSupplier(payload) && !hasPoOrGrnEvidence(payload);
  }
  if (bucket === 'POST_FAILED' && hasServicePreflight(payload)) return true;
  // Legacy fallback for rows still on the pre-bucket-contract intake shape.
  return (
    hasResolvedSupplier(payload) &&
    hasCanonicalNoPostableGrnBucket(payload, summary) &&
    !hasPoOrGrnEvidence(payload)
  );
}
